import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { useLanguage } from '../components/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  User, 
  ArrowRight, 
  Search,
  Filter,
  Heart,
  MessageCircle,
  Share2,
  Camera,
  Sparkles,
  TrendingUp,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const blogPosts = [
  {
    id: 1,
    title_he: 'המהפכה של תמונות AI באירועים: מה שאתה צריך לדעת',
    title_en: 'The AI Photo Revolution at Events: What You Need to Know',
    excerpt_he: 'גלה כיצד תמונות AI משנות את עולם האירועים והצילומים, ומדוע זהו העתיד של הצילום המקצועי.',
    excerpt_en: 'Discover how AI photos are changing the world of events and photography, and why this is the future of professional photography.',
    image: '/blog/ai-revolution.jpg',
    author: 'דניאל כהן',
    author_en: 'Daniel Cohen',
    date: '2024-01-15',
    readTime: '5 דקות',
    readTime_en: '5 min read',
    category: 'טכנולוגיה',
    category_en: 'Technology',
    likes: 234,
    comments: 45,
    featured: true
  },
  {
    id: 2,
    title_he: '10 טיפים לתמונות מושלמות בעמדת הצילום שלנו',
    title_en: '10 Tips for Perfect Photos at Our Photo Booth',
    excerpt_he: 'קבל את התוצאות הטובות ביותר מהחוויה שלנו עם הטיפים המקצועיים שלנו לתמונות מדהימות.',
    excerpt_en: 'Get the best results from our experience with our professional tips for amazing photos.',
    image: '/blog/perfect-photos.jpg',
    author: 'שרה לוי',
    author_en: 'Sarah Levi',
    date: '2024-01-10',
    readTime: '3 דקות',
    readTime_en: '3 min read',
    category: 'מדריכים',
    category_en: 'Guides',
    likes: 189,
    comments: 23
  },
  {
    id: 3,
    title_he: 'איך בוחרים את הדמות המושלמת לצילום?',
    title_en: 'How to Choose the Perfect Character for Your Photo?',
    excerpt_he: 'מדריך מקיף לבחירת הדמות המתאימה ביותר לסגנון שלך ולאירוע שלך.',
    excerpt_en: 'A comprehensive guide to choosing the most suitable character for your style and event.',
    image: '/blog/choose-character.jpg',
    author: 'יוסי גולן',
    author_en: 'Yossi Golan',
    date: '2024-01-05',
    readTime: '4 דקות',
    readTime_en: '4 min read',
    category: 'השראה',
    category_en: 'Inspiration',
    likes: 156,
    comments: 18
  },
  {
    id: 4,
    title_he: 'ההיסטוריה של תמונות הפוטו בות: מההתחלה ועד AI',
    title_en: 'The History of Photo Booths: From the Beginning to AI',
    excerpt_he: 'סיור מרתק בהתפתחות עמדות הצילום מהמצאתן ועד העידן הדיגיטלי המודרני.',
    excerpt_en: 'A fascinating tour of the evolution of photo booths from their invention to the modern digital age.',
    image: '/blog/photo-booth-history.jpg',
    author: 'משה כהן',
    author_en: 'Moshe Cohen',
    date: '2023-12-28',
    readTime: '6 דקות',
    readTime_en: '6 min read',
    category: 'היסטוריה',
    category_en: 'History',
    likes: 298,
    comments: 34
  },
  {
    id: 5,
    title_he: 'מאחורי הקלעים: כך עובדת הטכנולוגיה שלנו',
    title_en: 'Behind the Scenes: How Our Technology Works',
    excerpt_he: 'הצצה לטכנולוגיה המתקדמת שמניעה את הקסם שלנו והופכת תמונות ליצירות אמנות.',
    excerpt_en: 'A peek into the advanced technology that powers our magic and turns photos into works of art.',
    image: '/blog/behind-tech.jpg',
    author: 'דניאל כהן',
    author_en: 'Daniel Cohen',
    date: '2023-12-20',
    readTime: '7 דקות',
    readTime_en: '7 min read',
    category: 'טכנולוגיה',
    category_en: 'Technology',
    likes: 412,
    comments: 56
  },
  {
    id: 6,
    title_he: 'חתונות, בר מצוות ואירועים: רעיונות לצילומים בלתי נשכחים',
    title_en: 'Weddings, Bar Mitzvahs and Events: Ideas for Unforgettable Photos',
    excerpt_he: 'השראה ורעיונות יצירתיים להפוך את האירוע שלך לחוויה צילומית מיוחדת.',
    excerpt_en: 'Inspiration and creative ideas to make your event a special photographic experience.',
    image: '/blog/events-ideas.jpg',
    author: 'שרה לוי',
    author_en: 'Sarah Levi',
    date: '2023-12-15',
    readTime: '5 דקות',
    readTime_en: '5 min read',
    category: 'אירועים',
    category_en: 'Events',
    likes: 567,
    comments: 89,
    featured: true
  }
];

const categories = [
  { name_he: 'הכל', name_en: 'All', count: 0 },
  { name_he: 'טכנולוגיה', name_en: 'Technology', count: 2 },
  { name_he: 'מדריכים', name_en: 'Guides', count: 1 },
  { name_he: 'השראה', name_en: 'Inspiration', count: 1 },
  { name_he: 'היסטוריה', name_en: 'History', count: 1 },
  { name_he: 'אירועים', name_en: 'Events', count: 1 }
];

export default function Blog() {
  const { language, isRTL } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('הכל');
  const [posts, setPosts] = useState(blogPosts);

  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchTerm === '' || 
      (language === 'he' 
        ? post.title_he.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.excerpt_he.toLowerCase().includes(searchTerm.toLowerCase())
        : post.title_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.excerpt_en.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesCategory = selectedCategory === 'הכל' || 
      (language === 'he' 
        ? post.category === selectedCategory
        : post.category_en === selectedCategory
      );
    
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = posts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  const BlogCard = ({ post, featured = false }) => (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group cursor-pointer ${featured ? 'md:col-span-2' : ''}`}
    >
      <Link to={`${createPageUrl('Blog')}/${post.id}`} className="block">
        <div className="glass-card rounded-2xl overflow-hidden h-full transition-all duration-300 hover:border-violet-500/50">
          <div className={`relative ${featured ? 'h-64' : 'h-48'}`}>
            <img 
              src={post.image} 
              alt={language === 'he' ? post.title_he : post.title_en}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1 rounded-full bg-violet-600/90 text-white text-xs font-medium">
                {language === 'he' ? post.category : post.category_en}
              </span>
            </div>
            {featured && (
              <div className="absolute top-4 right-4">
                <Sparkles className="w-5 h-5 text-yellow-400" />
              </div>
            )}
          </div>
          
          <div className="p-6">
            <div className="flex items-center gap-4 text-sm text-slate-400 mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{new Date(post.date).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US')}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{language === 'he' ? post.readTime : post.readTime_en}</span>
              </div>
            </div>
            
            <h3 className={`font-bold text-white mb-2 group-hover:text-violet-400 transition-colors ${
              featured ? 'text-2xl' : 'text-xl'
            }`}>
              {language === 'he' ? post.title_he : post.title_en}
            </h3>
            
            <p className="text-slate-400 mb-4 line-clamp-2">
              {language === 'he' ? post.excerpt_he : post.excerpt_en}
            </p>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-violet-600/20 flex items-center justify-center">
                  <User className="w-4 h-4 text-violet-400" />
                </div>
                <span className="text-sm text-slate-300">
                  {language === 'he' ? post.author : post.author_en}
                </span>
              </div>
              
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <div className="flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  <span>{post.likes}</span>
                </div>
                <div className="flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" />
                  <span>{post.comments}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
            <Camera className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-medium text-violet-400">
              {language === 'he' ? 'הבלוג שלנו' : 'Our Blog'}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {language === 'he' ? 'תובנות והשראה מהעולם שלנו' : 'Insights and Inspiration from Our World'}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {language === 'he' 
              ? 'גלה טיפים, טרנדים וסיפורים מהחזית של הצילום והטכנולוגיה'
              : 'Discover tips, trends, and stories from the forefront of photography and technology'
            }
          </p>
        </motion.div>

        {/* Search and Filter */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder={language === 'he' ? 'חפש מאמרים...' : 'Search articles...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-slate-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-slate-800/50 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-violet-500"
              >
                {categories.map((cat) => (
                  <option key={cat.name_he} value={cat.name_he}>
                    {language === 'he' ? cat.name_he : cat.name_en}
                    {cat.count > 0 && ` (${cat.count})`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-yellow-400" />
              {language === 'he' ? 'מאמרים מומלצים' : 'Featured Articles'}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {featuredPosts.map((post) => (
                <BlogCard key={post.id} post={post} featured />
              ))}
            </div>
          </div>
        )}

        {/* Regular Posts Grid */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6">
            {language === 'he' ? 'כל המאמרים' : 'All Articles'}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularPosts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </div>

        {/* Load More */}
        <div className="text-center mt-12">
          <Button
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            {language === 'he' ? 'טען עוד מאמרים' : 'Load More Articles'}
            <ArrowRight className={`w-4 h-4 mr-2 ${isRTL ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Newsletter Signup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 glass-card rounded-2xl p-8 text-center"
        >
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
              <TrendingUp className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-medium text-violet-400">
                {language === 'he' ? 'הירשם לניוזלטר' : 'Subscribe to Newsletter'}
              </span>
            </div>
            
            <h3 className="text-2xl font-bold text-white mb-4">
              {language === 'he' 
                ? 'קבל את העדכונים האחרונים ישירות למייל'
                : 'Get the latest updates straight to your inbox'
              }
            </h3>
            
            <p className="text-slate-400 mb-6">
              {language === 'he' 
                ? 'טיפים בלעדיים, הנחות וחדשות מהעולם שלנו'
                : 'Exclusive tips, discounts, and news from our world'
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder={language === 'he' ? 'כתובת אימייל' : 'Email address'}
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
              />
              <Button className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white">
                {language === 'he' ? 'הירשם' : 'Subscribe'}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
